/**
 * Page Setup Context and Provider
 *
 * This module provides React Context for managing Page Setup state and Stepper walkthrough within Auriga.
 * Create Page final save and persistence will be handled here as well.
 */

import { TemplateType } from '@components/Common/CommonEnums';
import { DEFAULT_PAGE_DRIVERS } from '@constants/page';
import { useDeckContext } from '@context/DeckContext';
import { useTOCContext } from '@context/TOCContext';
import { useUserContext } from '@context/UserContext';
import { createId } from '@paralleldrive/cuid2';
import {
  DeckColumn,
  DeckColumnDeleteItemsInput,
  DeckColumnItemsInput,
  DenominatorLabelValues,
  KPIDrivers,
  Page,
} from '@services/api/deckManagement/graphql/schema/API';
import { useSaveDeck, UseSaveDeckParams } from '@services/api/deckManagement/hooks';
import { useGetDecklines, useGetPagesByPageName } from '@services/api/deckManagement/hooks/usePage';
import { entityKeyAsId, parseIdFromKey } from '@utils/deckHelpers';
import {
  applyPageDriverChangesToDecklines,
  determineDecklineType,
  extractActualKpiName,
  sanitizeDecklineData,
} from '@utils/decklineHelpers';
import { initializeMetadata, prepareEntriesForSaveDeckOperation } from '@utils/deckManagementEntityHelpers';
import { diffKPIDrivers, kpiDriversEmpty } from '@utils/kpiDriverHelpers';
import getNumberOfBlockHeaders from '@utils/templateHelpers';
import { forEach, isEmpty, isEqual, isNil, partition } from 'lodash';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChangeSetTargets, SaveDeckChangeSetSchema } from 'src/schema';
import { LockStatus } from 'types/DeckContext.types';
import { EnhancedDeckline } from 'types/DecklinesContext.types';
import { PageGranularity } from 'types/Page.types';
import {
  PageSetupContextType,
  PageSetupProviderProps,
  PageSetupSteps,
  SectionTemplateConfig,
  TableConfigState,
  TemplateCRUDItems,
} from 'types/PageSetupContext.types';

/**
 * Default ordering of Page Setup steps
 */
const PAGE_SETUP_STEP_ORDER: PageSetupSteps[] = [
  PageSetupSteps.PAGE_DETAILS,
  PageSetupSteps.TABLE_CONFIGURATION,
  PageSetupSteps.PAGE_DRIVER,
];

export const PageSetupContext = createContext<PageSetupContextType>({
  pageSetupSteps: Object.values(PageSetupSteps),
  currentStep: PageSetupSteps.PAGE_DETAILS,
  nextStep: PageSetupSteps.TABLE_CONFIGURATION,
  setCurrentStep: () => {},
  currDeckId: null,
  currPageName: null,
  tocId: null,
  setTocId: () => {},
  tocEntry: undefined,
  isPageCreated: false,
  setIsPageCreated: () => {},
  pageName: '',
  setPageName: () => {},
  tocName: '',
  setTocName: () => {},
  pageHeaderName: '',
  setPageHeaderName: () => {},
  reportingPeriods: [],
  setReportingPeriods: () => {},
  denominatorLabel: null,
  setDenominatorLabel: () => {},
  templateType: TemplateType.SINGLE,
  setTemplateType: () => {},
  numberOfBlockHeaders: 1,
  setNumberOfBlockHeaders: () => {},
  selectedTemplate: '',
  setSelectedTemplate: () => {},
  selectedMapping: '',
  setSelectedMapping: () => {},
  blockHeaders: {},
  setBlockHeaders: () => {},
  grainTemplates: {},
  setGrainTemplates: () => {},
  grainMappings: {},
  setGrainMappings: () => {},
  grainBlockHeaders: {},
  setGrainBlockHeaders: () => {},
  pageSections: [],
  activePageSection: '1',
  setActivePageSection: () => {},
  sectionTemplateConfigs: {},
  templateCRUDItems: { create: [], update: [], delete: [] },
  hasTableConfigChanged: false,
  snapshotTableConfig: () => {},
  resetTableConfig: () => {},
  isUpdateTemplate: false,
  setIsUpdateTemplate: () => {},
  availableGrains: [],
  pageDrivers: DEFAULT_PAGE_DRIVERS,
  setPageDrivers: () => {},
  savePageDrivers: () => {},
  pageDriversSaving: false,
  isDecklinesLoading: false,
  pageError: null,
  pageLockStatus: { isLocked: false },
});

export const PageSetupContextProvider = ({ children }: PageSetupProviderProps): ReactNode => {
  const { username } = useUserContext();
  const { currentDeckId, currentDeck, rootNodes } = useDeckContext();
  const { createNewPage, getTocItemsByGroup, hasInitialDataLoaded, deckColumnsForDeck, updateTocEntry } =
    useTOCContext();
  const saveDeckMutation = useSaveDeck();
  const saveDeckMutationRef = useRef(saveDeckMutation);
  saveDeckMutationRef.current = saveDeckMutation;

  // All TOC entries across groups (unfiltered)
  const allTocEntries = useMemo(() => Array.from(getTocItemsByGroup().values()).flat(), [getTocItemsByGroup]);
  // Get pageName from route parameters, if exists (will not exist from Page Library view)
  const { pageName: routePageName } = useParams<{ pageName: string }>();
  const currPageName = decodeURIComponent(routePageName || '');

  // TODO: will need to be dynamically controlled once we support the Page Denominator conditional step.
  const pageSetupSteps = useMemo(() => PAGE_SETUP_STEP_ORDER, []);

  // State for Page Setup Wizard handling
  const [currentStep, setCurrentStep] = useState<PageSetupSteps>(PAGE_SETUP_STEP_ORDER[0]);
  const nextStep = useMemo(() => {
    const currStepIdx = pageSetupSteps.indexOf(currentStep);
    return pageSetupSteps[currStepIdx + 1] ?? null;
  }, [currentStep, pageSetupSteps]);

  // Page Details Step state
  const [tocId, setTocId] = useState<string | null>(null);
  // Resolved TOC entry matching the current tocId
  const tocEntry = useMemo(() => allTocEntries.find((entry) => entry.toc_id === tocId), [allTocEntries, tocId]);
  const [isPageCreated, setIsPageCreated] = useState(false);
  const [pageName, setPageName] = useState('');
  const [tocName, setTocName] = useState('');
  const [pageHeaderName, setPageHeaderName] = useState('');
  const [reportingPeriods, setReportingPeriods] = useState<PageGranularity[]>([]);
  const [denominatorLabel, setDenominatorLabel] = useState<DenominatorLabelValues | null>(null);

  // Table Configuration Step state
  const [templateType, setTemplateType] = useState<TemplateType>(TemplateType.SINGLE);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [grainTemplates, setGrainTemplates] = useState<Record<string, string>>({});
  const [grainMappings, setGrainMappings] = useState<Record<string, string>>({});

  // Per-section configuration state (source of truth for mapping, block headers, and block count)
  const [activePageSection, setActivePageSection] = useState<string>('1');
  const [sectionTemplateConfigs, setSectionTemplateConfigs] = useState<Record<string, SectionTemplateConfig>>({
    '1': { mapping: '', blockHeaders: {}, numberOfBlockHeaders: 1 },
  });

  // Derived values from active section config (maintains backward-compatibility with existing component consumers)
  const selectedMapping = sectionTemplateConfigs[activePageSection]?.mapping || '';
  /** Number of block headers to assign to Page (Max determined by MAX_BLOCK_HEADERS in TableConfiguration Component; 1 by default) */
  const numberOfBlockHeaders = sectionTemplateConfigs[activePageSection]?.numberOfBlockHeaders || 1;
  const blockHeaders = useMemo(
    () => sectionTemplateConfigs[activePageSection]?.blockHeaders || {},
    [activePageSection, sectionTemplateConfigs],
  );

  /**
   * Sets the selected mapping for the active page section
   * @param mappingOrUpdater - Either the new mapping value or a function that takes the current value and returns the new value
   */
  const setSelectedMapping = useCallback(
    (mappingOrUpdater: string | ((prev: string) => string)) => {
      setSectionTemplateConfigs((prev) => {
        const current = prev[activePageSection]?.mapping || '';
        const newValue = typeof mappingOrUpdater === 'function' ? mappingOrUpdater(current) : mappingOrUpdater;
        return { ...prev, [activePageSection]: { ...prev[activePageSection], mapping: newValue } };
      });
    },
    [activePageSection],
  );

  /** Sets the number of block headers for the active page section */
  const setNumberOfBlockHeaders = useCallback(
    (count: number) => {
      setSectionTemplateConfigs((configs) => ({
        ...configs,
        [activePageSection]: { ...configs[activePageSection], numberOfBlockHeaders: count },
      }));
    },
    [activePageSection],
  );

  /**
   * Sets the block headers for the active page section
   * @param headersOrUpdater - Either the new block headers value or a function that takes the current value and returns the new value
   */
  const setBlockHeaders = useCallback(
    (headersOrUpdater: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => {
      setSectionTemplateConfigs((prev) => {
        const current = prev[activePageSection]?.blockHeaders || {};
        const newValue = typeof headersOrUpdater === 'function' ? headersOrUpdater(current) : headersOrUpdater;
        return { ...prev, [activePageSection]: { ...prev[activePageSection], blockHeaders: newValue } };
      });
    },
    [activePageSection],
  );

  // Snapshot of initial table config selections for change detection
  const initialTableConfigRef = useRef<TableConfigState>({
    templateType: TemplateType.SINGLE,
    selectedTemplate: '',
    grainTemplates: {},
    grainMappings: {},
    grainBlockHeaders: {},
    sectionTemplateConfigs: { '1': { mapping: '', blockHeaders: {}, numberOfBlockHeaders: 1 } },
  });
  const [grainBlockHeaders, setGrainBlockHeaders] = useState<Record<string, Record<string, string>>>({});

  // Page Drivers Step state
  const [pageId, setPageId] = useState<string | null>(null);
  const [pageDrivers, setPageDrivers] = useState<KPIDrivers>(DEFAULT_PAGE_DRIVERS);
  const [pageError, setPageError] = useState<string | null>(null);
  const [pageDriversSaving, setPageDriversSaving] = useState<boolean>(false);
  const [originalPageDrivers, setOriginalPageDrivers] = useState<KPIDrivers>(DEFAULT_PAGE_DRIVERS);

  // Fetch decklines for the current page once pageId is available.
  // For new pages this returns an empty array; for existing pages it returns decklines for propagation.
  const { data: existingDecklines, isLoading: isDecklinesLoading } = useGetDecklines({
    pageId: pageId || '',
    options: {
      enabled: Boolean(pageId),
    },
  });

  // Enhance raw decklines with metadata for change tracking
  const enhancedDecklines = useMemo(() => {
    if (!existingDecklines || existingDecklines.length === 0) return [];

    return existingDecklines.map((deckline, idx) => {
      const sanitized = sanitizeDecklineData(deckline);
      return initializeMetadata<EnhancedDeckline>(
        {
          ...sanitized,
          deckline_id: parseIdFromKey(sanitized.sk),
          page_id: pageId || '',
          deckline_type: determineDecklineType(sanitized),
          actual_kpi_name: extractActualKpiName(sanitized),
          // Cast needed: Deckline.comments (Comment[]) is structurally compatible at runtime
          // but not assignable to EnhancedDeckline.comments (EnhancedComment[]) at the type level.
        } as Omit<EnhancedDeckline, 'metadata'>,
        { sortPos: idx },
      );
    });
  }, [existingDecklines, pageId]);

  // For tracking if we are dealing with an Update template request
  // Set to true after first template save, so subsequent saves use the update path
  // TODO: Needs additional expansion when arriving from Page View
  const [isUpdateTemplate, setIsUpdateTemplate] = useState(!isEmpty(currPageName));

  // Fetch grain pages for the current page name and deck (shared by Table Configuration + Page Drivers)
  // Gets enabled for edit workflow when currPageName exists from route, or after page creates in new page workflow
  const { data: pagesByName, isError: isPagesByNameError } = useGetPagesByPageName({
    page_name: pageName || currPageName,
    deck_id: currentDeckId || '',
    options: {
      enabled:
        (isPageCreated || !isEmpty(currPageName)) &&
        ((!isNil(pageName) && !isEmpty(pageName)) || !isEmpty(currPageName)) &&
        !isNil(currentDeckId) &&
        !isEmpty(currentDeckId),
    },
  });

  /**
   * Dynamically determine available grains from pagesByName
   */
  const availableGrains = useMemo(() => {
    if (!pagesByName || pagesByName.length === 0) return ['MTD', 'QTD', 'YTD'];

    const grains = pagesByName
      .map((page) => page.page_grain)
      .filter((grain): grain is string => Boolean(grain))
      .filter((grain, index, arr) => arr.indexOf(grain) === index);

    return grains.length > 0 ? grains : ['MTD', 'QTD', 'YTD'];
  }, [pagesByName]);

  /**
   * Per-page lock state for the page being set up (all grains of a logical page share one
   * lock). Editing the page-setup wizard requires holding THIS page's lock — independent
   * of the deck lock. Derived from the fetched grain records.
   */
  const pageLockStatus = useMemo<LockStatus>(() => {
    const lockedGrain = pagesByName?.find((page) => Boolean(page.locked_by));
    const lockedBy = lockedGrain?.locked_by ?? undefined;
    const isLocked = Boolean(lockedBy);
    const isLockedByCurrentUser = isLocked && lockedBy === username;
    return {
      isLocked,
      lockedBy,
      lockedAt: lockedGrain?.locked_at ?? undefined,
      lockExpiry: lockedGrain?.lock_expiry ?? undefined,
      isLockedByCurrentUser,
      isEditingDisabled: !isLockedByCurrentUser,
    };
  }, [pagesByName, username]);

  // Derived list of page sections (sorted) from available deck columns (e.g. ['1', '2', '3', '4', etc.] for single and multi-section pages)
  const pageSections = useMemo(() => {
    if (!deckColumnsForDeck || !pagesByName) return [];
    const pagePks = new Set(pagesByName.map((page) => page.pk));
    const sections = new Set(
      deckColumnsForDeck
        .filter((col) => pagePks.has(col.pk))
        .map((col) => col.page_section)
        .filter(Boolean),
    );
    return Array.from(sections).sort() as string[];
  }, [deckColumnsForDeck, pagesByName]);

  /**
   * Filters deck columns based on template and column mapping group criteria
   * @param template - Column template to filter by
   * @param columnMappingGroup - Column mapping group to filter by
   * @returns Filtered array of deck columns excluding 'Segment Summary P&L Results'
   */
  const filterDeckColumns = useCallback(
    (template: string, columnMappingGroup: string): DeckColumn[] => {
      if (!deckColumnsForDeck || !Array.isArray(deckColumnsForDeck)) return [];

      return deckColumnsForDeck.filter((column) => {
        if (template && column?.column_template !== template) {
          return false;
        }
        if (columnMappingGroup && column?.column_mapping_group !== columnMappingGroup) {
          return false;
        }
        return column?.report_header !== 'Segment Summary P&L Results';
      });
    },
    [deckColumnsForDeck],
  );

  /**
   * Groups filtered deck columns by their block_id
   * @param filteredDeckColumns - Array of filtered deck columns
   * @returns Record mapping block_id to arrays of deck columns
   */
  const groupDeckColumns = useCallback((filteredDeckColumns: DeckColumn[]): Record<string, DeckColumn[]> => {
    const groups: Record<string, DeckColumn[]> = {};

    filteredDeckColumns.forEach((column) => {
      const key = `${column?.column_name || 'unknown'}_${column?.scenario_display_name || 'unknown'}_${column?.scenario_period || 'unknown'}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(column);
    });

    return groups;
  }, []);

  /**
   * Creates a new deck column item based on the provided parameters
   * @param page - The page associated with the deck column item
   * @param firstColumn - The first deck column to use as template information
   * @param template - The column template
   * @param pageSection - The page section (currently only 1 section is supported)
   * @param mapping - The column mapping group
   * @param blockHeader - The block header to apply to the column
   * @param reportHeader - The report header for the Page
   * @param timeGrain - The time grain to associate with the column
   */
  const createDeckColumnItem = (
    page: Page,
    firstColumn: DeckColumn,
    template: string,
    pageSection: string,
    mapping: string,
    blockHeader: string,
    reportHeader: string,
    timeGrain: string | undefined | null,
  ): DeckColumnItemsInput => ({
    ...firstColumn,
    page_id: parseIdFromKey(page.pk),
    deckcolumn_id: `${firstColumn.column_no}_${createId()}`,
    column_template: template,
    page_section: pageSection,
    column_mapping_group: mapping,
    block_header: blockHeader,
    report_header: reportHeader,
    time_grain: timeGrain,
  });

  /**
   * Creates an updated deck column item list based on provided parameters
   * @param page - The page associated with the deck column item
   * @param deckColumns - The deckColumns group to update
   * @param template - The column template
   * @param pageSection - The page section (currently only 1 section is supported)
   * @param mapping - The column mapping group
   * @param blockHeader - The block header to apply to the column
   * @param reportHeader - The report header for the Page
   */
  const updateDeckColumnItem = (
    page: Page,
    deckColumns: DeckColumn[],
    template: string,
    pageSection: string,
    mapping: string,
    blockHeader: string,
    reportHeader: string,
  ): DeckColumnItemsInput[] => {
    const deckColumnsItemInput: DeckColumnItemsInput[] = [];

    forEach(deckColumns, (deckColumn: DeckColumn) => {
      deckColumnsItemInput.push({
        ...deckColumn,
        page_id: parseIdFromKey(page.pk),
        deckcolumn_id: parseIdFromKey(deckColumn.sk),
        column_template: template,
        page_section: pageSection,
        column_mapping_group: mapping,
        block_header: blockHeader,
        report_header: reportHeader,
      });
    });

    return deckColumnsItemInput;
  };

  /**
   * Creates a deletable deck column item list based on provided parameters
   * @param page - The page associated with the deck column item
   * @param deckColumns - The deckColumns to delete
   */
  const deleteDeckColumnItem = (page: Page, deckColumns: DeckColumn[]): DeckColumnDeleteItemsInput[] => {
    const deckColumnsDeleteItemInput: DeckColumnDeleteItemsInput[] = [];

    forEach(deckColumns, (deckColumn: DeckColumn) => {
      deckColumnsDeleteItemInput.push({
        page_id: parseIdFromKey(page.pk),
        deckcolumn_id: parseIdFromKey(deckColumn.sk),
      });
    });

    return deckColumnsDeleteItemInput;
  };

  /**
   * Processes deck columns for a given template and mapping, creating, updating, or deleting items as needed
   * @param pages - The pages to process columns for
   * @param template - The column template to filter by
   * @param mapping - The column mapping group to filter by
   * @param getBlockHeader - Function to determine the block header for a given column
   * @param reportHeader - The report header for the Page
   * @param pageSection - The page section
   * @returns TemplateCRUDItems object containing create, update, and delete lists
   */
  const processColumnsForTemplate = useCallback(
    (
      pages: Page[],
      template: string,
      mapping: string,
      getBlockHeader: (firstColumn: DeckColumn) => string,
      reportHeader: string,
      pageSection: string = '1', // Default to single section
      blockHeaderCount: number = 1,
    ): TemplateCRUDItems => {
      const deckColumnItemsCreateList: Array<DeckColumnItemsInput> = [];
      const deckColumnItemsUpdateList: Array<DeckColumnItemsInput> = [];
      const deckColumnItemsDeleteList: Array<DeckColumnDeleteItemsInput> = [];

      // Filter columns based on template, mapping, and page section; partition by number of block headers
      const sectionColumns = filterDeckColumns(template, mapping).filter((col) => col.page_section === pageSection);
      const [matchedDeckColumns, unMatchedDeckColumns] = partition(
        sectionColumns,
        (deckColumn) => (deckColumn.block_id || 1) <= blockHeaderCount,
      );
      // Group columns for final usage
      const groupedMatchColumns = groupDeckColumns(matchedDeckColumns);
      const groupedUnMatchedColumns = groupDeckColumns(unMatchedDeckColumns);

      pages?.forEach((page) => {
        Object.entries(groupedMatchColumns).forEach(([, columns]) => {
          const firstColumn = columns[0];

          if (!isUpdateTemplate) {
            // Handle CREATE case
            const createDeckColumnItemsInput: DeckColumnItemsInput = createDeckColumnItem(
              page,
              firstColumn,
              template,
              pageSection,
              mapping,
              getBlockHeader(firstColumn),
              reportHeader,
              firstColumn.time_grain,
            );

            // Add the deck columns item input to the tracking list
            deckColumnItemsCreateList.push(createDeckColumnItemsInput);
          } else {
            const existingPageColumns = columns.filter((column) => column.pk === page.pk); // Only send column that match Page grains when updating

            if (existingPageColumns.length > 0) {
              // Handle UPDATE case
              const updateDeckColumnItemsInput: DeckColumnItemsInput[] = updateDeckColumnItem(
                page,
                existingPageColumns,
                template,
                pageSection,
                mapping,
                getBlockHeader(firstColumn),
                reportHeader,
              );

              // Add the update deck columns item input to the tracking list
              deckColumnItemsUpdateList.push(...updateDeckColumnItemsInput);
            } else {
              // Handle CREATE case — new block header added during update
              const createDeckColumnItemsInput: DeckColumnItemsInput = createDeckColumnItem(
                page,
                firstColumn,
                template,
                pageSection,
                mapping,
                getBlockHeader(firstColumn),
                reportHeader,
                firstColumn.time_grain,
              );

              // Add the create deck columns item input to the tracking list
              deckColumnItemsCreateList.push(createDeckColumnItemsInput);
            }
          }
        });

        // Handle any unmatched group columns that need to be DELETED on update
        if (isUpdateTemplate) {
          Object.entries(groupedUnMatchedColumns).forEach(([, columns]) => {
            // Collect DELETE items
            const deleteDeckColumnItemsInput: DeckColumnDeleteItemsInput[] = deleteDeckColumnItem(
              page,
              columns.filter((column) => column.pk === page.pk), // Only send column that match Page grains when deleting
            );

            // Add the delete deck columns item input to the tracking list
            deckColumnItemsDeleteList.push(...deleteDeckColumnItemsInput);
          });
        }
      });

      return {
        create: deckColumnItemsCreateList,
        update: deckColumnItemsUpdateList,
        delete: deckColumnItemsDeleteList,
      };
    },
    [filterDeckColumns, groupDeckColumns, isUpdateTemplate],
  );

  /**
   * Creates, Updates, or Deletes deck column items for single template type
   * Uses the same template and mapping for all pages
   * @returns TemplateCRUDItems object containing create, update, and delete lists for single template configuration
   */
  const deckColumnsSingleTemplateCRUD: TemplateCRUDItems = useMemo(() => {
    if (templateType !== TemplateType.SINGLE || !selectedTemplate) return { create: [], update: [], delete: [] };

    // Handles all available sections (single or multi-section page support)
    const sections = Object.entries(sectionTemplateConfigs);
    if (sections.length === 0 || sections.every(([, config]) => !config.mapping)) {
      return { create: [], update: [], delete: [] };
    }

    // Iterate all sections and aggregate CRUD items
    const allCreate: DeckColumnItemsInput[] = [];
    const allUpdate: DeckColumnItemsInput[] = [];
    const allDelete: DeckColumnDeleteItemsInput[] = [];
    sections.forEach(([section, config]) => {
      if (!config.mapping) return;
      const result = processColumnsForTemplate(
        pagesByName || [],
        selectedTemplate,
        config.mapping,
        (firstColumn) => config.blockHeaders[`blockHeader${firstColumn.block_id}`] || '',
        pageHeaderName || pageName, // Use updated Page Header Name if defined, or default to Page Name
        section,
        config.numberOfBlockHeaders,
      );
      allCreate.push(...result.create);
      allUpdate.push(...result.update);
      allDelete.push(...result.delete);
    });
    return { create: allCreate, update: allUpdate, delete: allDelete };
  }, [
    templateType,
    processColumnsForTemplate,
    pagesByName,
    selectedTemplate,
    pageHeaderName,
    pageName,
    sectionTemplateConfigs,
  ]);

  /**
   * Creates, Updates, or Deletes deck column items for granular template type
   * Processes each page with its specific grain template and mapping
   * @returns TemplateCRUDItems object containing create, update, and delete lists for granular template configuration
   */
  const deckColumnsGranularTemplateCRUD: TemplateCRUDItems = useMemo(() => {
    if (templateType !== TemplateType.GRANULAR || isEmpty(grainTemplates) || isEmpty(grainMappings))
      return { create: [], update: [], delete: [] };

    const deckColCreateItems: DeckColumnItemsInput[] = [];
    const deckColUpdateItems: DeckColumnItemsInput[] = [];
    const deckColDeleteItems: DeckColumnDeleteItemsInput[] = [];

    forEach(pagesByName, (page) => {
      const pageGrain = page.page_grain;
      const grainTemplate = grainTemplates[pageGrain!] || '';
      const grainMapping = grainMappings[pageGrain!] || '';

      const result = processColumnsForTemplate(
        [page],
        grainTemplate,
        grainMapping,
        (firstColumn) => grainBlockHeaders[pageGrain!]?.[`blockHeader${firstColumn.block_id}`] || '',
        pageHeaderName || pageName, // Use updated Page Header Name if defined, or default to Page Name
        '1', // Granular mode only supports single section
        numberOfBlockHeaders,
      );

      deckColCreateItems.push(...result.create);
      deckColUpdateItems.push(...result.update);
      deckColDeleteItems.push(...result.delete);
    });

    return { create: deckColCreateItems, update: deckColUpdateItems, delete: deckColDeleteItems };
  }, [
    templateType,
    pagesByName,
    grainTemplates,
    grainMappings,
    processColumnsForTemplate,
    pageHeaderName,
    pageName,
    grainBlockHeaders,
    numberOfBlockHeaders,
  ]);

  /** Computed CRUD changeset based on current template type selection */
  const templateCRUDItems: TemplateCRUDItems = useMemo(() => {
    return templateType === TemplateType.SINGLE ? deckColumnsSingleTemplateCRUD : deckColumnsGranularTemplateCRUD;
  }, [templateType, deckColumnsSingleTemplateCRUD, deckColumnsGranularTemplateCRUD]);

  // Track whether initial TOC entry creation has been triggered for New Page flow
  const hasInitialized = useRef(false);
  const hasPopulatedDefaults = useRef(false);

  // On mount for New Page flow, create a TOC entry once TOC data has loaded
  useEffect(() => {
    if (isEmpty(currPageName) && !tocId && !hasInitialized.current && hasInitialDataLoaded) {
      hasInitialized.current = true;
      createNewPage((newTocId: string) => {
        setTocId(newTocId);

        // Add an incomplete identifier to the otherwise unused group_name field, so we can filter out entries from
        // the side menu, and add information in the case of early navigation to the page view.
        // Follows the format: SETUP_INCOMPLETE|CURRENT_STEP
        updateTocEntry(newTocId, 'group_name', `SETUP_INCOMPLETE|${PageSetupSteps.PAGE_DETAILS}`);
      });
    }
  }, [currPageName, tocId, createNewPage, hasInitialDataLoaded, updateTocEntry]);

  // Prepopulate form fields from the created TOC entry once available (New Page Flow)
  useEffect(() => {
    if (tocId && isEmpty(currPageName) && !hasPopulatedDefaults.current) {
      const createdEntry = allTocEntries.find((entry) => entry.toc_id === tocId);
      if (createdEntry) {
        hasPopulatedDefaults.current = true;
        setPageName(createdEntry.page_name || '');
        setTocName(createdEntry.toc_page_name || '');
        setPageHeaderName(createdEntry.page_name || '');
      }
    }
  }, [tocId, allTocEntries, currPageName]);

  // Prepopulate form fields from existing page data (Edit Page Flow)
  useEffect(() => {
    if (isEmpty(currPageName) || hasPopulatedDefaults.current) return;
    if (!deckColumnsForDeck || allTocEntries.length === 0) return;

    // Page and TOC names — populate from TOC entry regardless of pagesByName state
    const tocItem = allTocEntries.find((entry) => entry.page_name === currPageName);
    setPageName(currPageName);
    setTocName(tocItem?.toc_page_name || 'Unknown TOC Name');

    // If no Page entity exists in the backend, mark defaults as populated and exit early
    // Note: In this case, Save Page in Page Driver setup will precent Save due to no Pages existing from getPagesByName()
    if (!pagesByName || pagesByName.length === 0) {
      hasPopulatedDefaults.current = true;
      return;
    }

    // Gather primary keys from PagesByName call to filter DeckColumn information
    const pagePks = new Set(pagesByName.map((page) => page.pk));
    // Filtered deck columns for a Page (excluding 'Segment Summary P&L Results') to match deprecated ColumnTemplateDialog
    const allPageColumns = deckColumnsForDeck.filter((col) => {
      return pagePks.has(col.pk) && col?.report_header !== 'Segment Summary P&L Results';
    });

    // Handle case where Page exists in Page Library, but has no columns setup yet and needs Page Setup Wizard to work
    if (allPageColumns.length === 0) {
      hasPopulatedDefaults.current = true;
      return;
    }

    // Build per-section template configs (always populated regardless of single or multi-section page)
    const detectedPageSections = new Set(allPageColumns.map((col) => col.page_section).filter(Boolean));
    const configs: Record<string, SectionTemplateConfig> = {};
    detectedPageSections.forEach((section) => {
      // Process relevant deck columns for the section
      const sectionCols = allPageColumns.filter((col) => col.page_section === section);
      const sectionBlockHeaders: Record<string, string> = {};
      forEach(sectionCols, (col) => {
        const blockId = col.block_id || 1;
        const key = `blockHeader${blockId}`;
        if (col.block_header && !sectionBlockHeaders[key]) {
          sectionBlockHeaders[key] = col.block_header;
        }
      });
      configs[section as string] = {
        mapping: sectionCols[0]?.column_mapping_group || '',
        blockHeaders: sectionBlockHeaders,
        numberOfBlockHeaders: getNumberOfBlockHeaders(sectionCols),
      };
    });
    setSectionTemplateConfigs(configs);

    // Use first section columns for shared template and defaults
    const firstSectionKey = Array.from(detectedPageSections).sort()[0];
    setActivePageSection(firstSectionKey as string);
    const pageColumns = allPageColumns.filter((col) => col.page_section === firstSectionKey);

    // Set true to prevent this useEffect from running subsequent times from Edit Page flow
    hasPopulatedDefaults.current = true;

    // Page Header when available from columns that exist
    setPageHeaderName(pageColumns[0]?.report_header || 'Unknown Page Header');
    setIsPageCreated(true);

    // Set template and mapping from final filtered page deck columns
    setSelectedTemplate(pageColumns[0]?.column_template || '');

    // Capture initial state for change detection on subsequent saves
    initialTableConfigRef.current = {
      templateType,
      selectedTemplate: pageColumns[0]?.column_template || '',
      grainTemplates: {},
      grainMappings: {},
      grainBlockHeaders: {},
      sectionTemplateConfigs: configs,
    };
  }, [currPageName, pagesByName, deckColumnsForDeck, allTocEntries, templateType]);

  /**
   * Handles saving Page Drivers, and Page denominator_label updates back to the Deck.
   * For existing pages with decklines, computes the diff between original and current page drivers,
   * applies changes to decklines, and includes both page and deckline updates in the SaveDeck changeset.
   * @param successCallback - Optional callback function to execute on successful save
   * @param failureCallback - Optional callback function to execute on save failure
   */
  const savePageDrivers = useCallback(
    (successCallback?: () => void, failureCallback?: (error: Error) => void) => {
      if (!pageId || !currentDeckId) {
        failureCallback?.(new Error('Page data not yet loaded.'));
        return;
      }

      setPageDriversSaving(true);

      const changeSet: Record<string, unknown> = {
        [ChangeSetTargets.page]: {
          update: [
            {
              page_id: pageId,
              page_drivers: { ...pageDrivers },
              denominator_label: denominatorLabel, // Carried over from user assignment at Page Details step
            },
          ],
        },
      };

      // For existing pages with decklines, compute and include deckline updates
      const updates = diffKPIDrivers(originalPageDrivers, pageDrivers);
      if (Object.keys(updates).length > 0 && enhancedDecklines.length > 0) {
        const updatedDecklines = applyPageDriverChangesToDecklines(
          enhancedDecklines,
          originalPageDrivers,
          updates,
          rootNodes,
        );

        const preparedEntries = prepareEntriesForSaveDeckOperation(updatedDecklines, []);
        if (Object.values(preparedEntries).some((entries) => entries.length > 0)) {
          changeSet[ChangeSetTargets.deckline] = preparedEntries;
        }
      }

      const validatedChangeSet = SaveDeckChangeSetSchema.parse(changeSet);

      const saveDeckParams: UseSaveDeckParams = {
        deckId: currentDeckId || undefined,
        deckName: currentDeck?.deck_name || undefined,
        change_set: validatedChangeSet,
        currentUser: username,
        successCallback: () => {
          setOriginalPageDrivers(pageDrivers);
          successCallback?.();
        },
      };

      saveDeckMutationRef.current.mutate(saveDeckParams, {
        onError: (saveError) => {
          console.error('Save operation failed:', saveError);
          failureCallback?.(saveError);
        },
        onSettled: () => {
          setPageDriversSaving(false);
        },
      });
    },
    [
      currentDeck?.deck_name,
      currentDeckId,
      denominatorLabel,
      enhancedDecklines,
      originalPageDrivers,
      pageDrivers,
      pageId,
      rootNodes,
      username,
    ],
  );

  // Determines if the table configuration has changed from its initial persisted state
  const hasTableConfigChanged = useMemo((): boolean => {
    const currentConfig: TableConfigState = {
      templateType,
      selectedTemplate,
      grainTemplates,
      grainMappings,
      grainBlockHeaders,
      sectionTemplateConfigs,
    };
    return !isEqual(currentConfig, initialTableConfigRef.current);
  }, [templateType, selectedTemplate, grainTemplates, grainMappings, grainBlockHeaders, sectionTemplateConfigs]);

  /** Updates the baseline snapshot to the current selections (call after a successful save) */
  const snapshotTableConfig = useCallback(() => {
    initialTableConfigRef.current = {
      templateType,
      selectedTemplate,
      grainTemplates,
      grainMappings,
      grainBlockHeaders,
      sectionTemplateConfigs,
    };
  }, [templateType, selectedTemplate, grainTemplates, grainMappings, grainBlockHeaders, sectionTemplateConfigs]);

  /** Restores table configuration selections to the initial persisted state */
  const resetTableConfig = useCallback(() => {
    const initial = initialTableConfigRef.current;
    setTemplateType(initial.templateType);
    setSelectedTemplate(initial.selectedTemplate);
    setSectionTemplateConfigs(initial.sectionTemplateConfigs);
    setGrainTemplates(initial.grainTemplates);
    setGrainMappings(initial.grainMappings);
    setGrainBlockHeaders(initial.grainBlockHeaders);
  }, []);

  /**
   * Effect to handle API GetPagesByPageName fetch errors
   */
  useEffect(() => {
    if (isPagesByNameError && pageName) {
      setPageError(`Failed to fetch page data for "${pageName}". Unable to proceed.`);
      setPageDrivers(DEFAULT_PAGE_DRIVERS);
    } else if (!isPagesByNameError && pageError) {
      // Clear error when API error state changes from error to success
      setPageError(null);
    }
  }, [isPagesByNameError, pageName, pageError]);

  /**
   * Handles setting the Page ID and Page Drivers when the page name changes
   */
  useEffect(() => {
    if (pagesByName && pagesByName.length > 0) {
      // NOTE: `page_drivers` are synchronized across all grains in the backend service.
      // Because of this, we can just use the first filtered (by Page Name) result to retrieve the drivers.
      const targetPage = pagesByName.filter((page) => page.page_name === pageName)[0];

      if (targetPage) {
        const parsedPageId = targetPage?.pk ? entityKeyAsId(targetPage?.pk) : null;
        const drivers = kpiDriversEmpty(targetPage.page_drivers) ? DEFAULT_PAGE_DRIVERS : targetPage.page_drivers!;
        setPageId(parsedPageId);
        setPageDrivers(drivers);
        setOriginalPageDrivers(drivers);
        // Only update denominatorLabel from backend on Edit flow to avoid overwriting any User selection during Create
        if (currPageName) {
          setDenominatorLabel(targetPage.denominator_label ?? null);
        }
      } else {
        setPageId(null);
        setPageError(`Page with Name "${pageName}" not found. Unable to proceed.`);
      }
    }
  }, [isPageCreated, pageName, pagesByName, currPageName]);

  const contextValue = useMemo<PageSetupContextType>(
    () => ({
      pageSetupSteps,
      currentStep,
      nextStep,
      setCurrentStep,
      currDeckId: currentDeckId,
      currPageName,
      tocId,
      setTocId,
      tocEntry,
      isPageCreated,
      setIsPageCreated,
      pageName,
      setPageName,
      tocName,
      setTocName,
      pageHeaderName,
      setPageHeaderName,
      reportingPeriods,
      setReportingPeriods,
      denominatorLabel,
      setDenominatorLabel,
      templateType,
      setTemplateType,
      numberOfBlockHeaders,
      setNumberOfBlockHeaders,
      selectedTemplate,
      setSelectedTemplate,
      selectedMapping,
      setSelectedMapping,
      blockHeaders,
      setBlockHeaders,
      grainTemplates,
      setGrainTemplates,
      grainMappings,
      setGrainMappings,
      grainBlockHeaders,
      setGrainBlockHeaders,
      pageSections,
      activePageSection,
      setActivePageSection,
      sectionTemplateConfigs,
      templateCRUDItems,
      hasTableConfigChanged,
      snapshotTableConfig,
      resetTableConfig,
      isUpdateTemplate,
      setIsUpdateTemplate,
      availableGrains,
      pageDrivers,
      setPageDrivers,
      savePageDrivers,
      pageDriversSaving,
      isDecklinesLoading,
      pageError,
      pageLockStatus,
    }),
    [
      pageSetupSteps,
      currentStep,
      nextStep,
      currentDeckId,
      currPageName,
      isPageCreated,
      pageName,
      pageHeaderName,
      reportingPeriods,
      denominatorLabel,
      templateType,
      numberOfBlockHeaders,
      setNumberOfBlockHeaders,
      selectedTemplate,
      selectedMapping,
      setSelectedMapping,
      blockHeaders,
      setBlockHeaders,
      grainTemplates,
      grainMappings,
      grainBlockHeaders,
      pageSections,
      activePageSection,
      sectionTemplateConfigs,
      templateCRUDItems,
      hasTableConfigChanged,
      snapshotTableConfig,
      resetTableConfig,
      isUpdateTemplate,
      availableGrains,
      tocId,
      tocEntry,
      tocName,
      pageDrivers,
      setPageDrivers,
      savePageDrivers,
      pageDriversSaving,
      isDecklinesLoading,
      pageError,
      pageLockStatus,
    ],
  );

  return <PageSetupContext value={contextValue}>{children}</PageSetupContext>;
};

/**
 * Custom hook to access Page Setup Context
 *
 * @returns {PageSetupContextType} The Page setup context value
 */
export const usePageSetupContext = (): PageSetupContextType => {
  return use(PageSetupContext);
};
